import { Suspense } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useParams,
} from "react-router-dom";
import { Toaster } from "sonner";
import { AuthLayout } from "./components/layouts/AuthLayout";
import { withNavigation } from "./components/layouts/PageWrapper";
import { useAuthContext } from "./hooks/AuthProvider";
import { ErrorBoundary, AppErrorFallback } from "./components/ui/ErrorBoundary";
import { LoadingPanel } from "./components/ui/LoadingPanel";
import { lazyNamed } from "./lib/lazyNamed";

// Pages lazy-loadees
const ConnexionPage = lazyNamed(
  () => import("./components/pages/ConnexionPage"),
  "ConnexionPage"
);
const ForgotPasswordPage = lazyNamed(
  () => import("./components/pages/ForgotPasswordPage"),
  "ForgotPasswordPage"
);
const ResetPasswordPage = lazyNamed(
  () => import("./components/pages/ResetPasswordPage"),
  "ResetPasswordPage"
);
const MfaEnrollPage = lazyNamed(
  () => import("./components/pages/MfaEnrollPage"),
  "MfaEnrollPage"
);
const MfaChallengePage = lazyNamed(
  () => import("./components/pages/MfaChallengePage"),
  "MfaChallengePage"
);
const MentionsLegalesPage = lazyNamed(
  () => import("./components/pages/MentionsLegalesPage"),
  "MentionsLegalesPage"
);
const ConditionsGeneralesPage = lazyNamed(
  () => import("./components/pages/ConditionsGeneralesPage"),
  "ConditionsGeneralesPage"
);
const PolitiqueConfidentialitePage = lazyNamed(
  () => import("./components/pages/PolitiqueConfidentialitePage"),
  "PolitiqueConfidentialitePage"
);
const CguAcceptationPage = lazyNamed(
  () => import("./components/pages/CguAcceptationPage"),
  "CguAcceptationPage"
);
const TableauDeBordAdminPage = lazyNamed(
  () => import("./components/pages/TableauDeBordAdminPage"),
  "TableauDeBordAdminPage"
);
const TableauDeBordPage = lazyNamed(
  () => import("./components/pages/TableauDeBordPage"),
  "TableauDeBordPage"
);
const MesChantiersPage = lazyNamed(
  () => import("./components/pages/MesChantiersPage"),
  "MesChantiersPage"
);
const ChantierDetailAdminPage = lazyNamed(
  () => import("./components/chantier-detail/ChantierDetailPage"),
  "ChantierDetailPage"
);
const ChantierDetailPage = lazyNamed(
  () => import("./components/pages/ChantierDetailPage"),
  "ChantierDetailPage"
);
const ChantierCreationPage = lazyNamed(
  () => import("./components/pages/ChantierCreationPage"),
  "ChantierCreationPage"
);
const DechetFormulaireCreationPage = lazyNamed(
  () => import("./components/pages/DechetFormulaireCreationPage"),
  "DechetFormulaireCreationPage"
);
const ChantierFormulaireCommandePage = lazyNamed(
  () => import("./components/pages/ChantierFormulaireCommandePage"),
  "ChantierFormulaireCommandePage"
);
const ChantierFormulaireDevisPage = lazyNamed(
  () => import("./components/pages/ChantierFormulaireDevisPage"),
  "ChantierFormulaireDevisPage"
);
const MesTransportsPage = lazyNamed(
  () => import("./components/pages/MesTransportsPage"),
  "MesTransportsPage"
);
const TransportCreationPage = lazyNamed(
  () => import("./components/pages/TransportCreationPage"),
  "TransportCreationPage"
);
const TransportFormulairePage = lazyNamed(
  () => import("./components/pages/TransportFormulairePage"),
  "TransportFormulairePage"
);
const TransportDetailAdminPage = lazyNamed(
  () => import("./components/pages/TransportDetailAdminPage"),
  "TransportDetailAdminPage"
);
const TransportDetailPage = lazyNamed(
  () => import("./components/pages/TransportDetailPage"),
  "TransportDetailPage"
);
const MesCAPPage = lazyNamed(
  () => import("./components/pages/MesCAPPage"),
  "MesCAPPage"
);
const MaFacturationPage = lazyNamed(
  () => import("./components/pages/MaFacturationPage"),
  "MaFacturationPage"
);
const FacturationAdminPage = lazyNamed(
  () => import("./components/pages/FacturationAdminPage"),
  "FacturationAdminPage"
);
const OperationsAdminPage = lazyNamed(
  () => import("./components/pages/OperationsAdminPage"),
  "OperationsAdminPage"
);
const EtablissementsAdminPage = lazyNamed(
  () => import("./components/pages/EtablissementsAdminPage"),
  "EtablissementsAdminPage"
);
const MonEtablissementPage = lazyNamed(
  () => import("./components/pages/MonEtablissementPage"),
  "MonEtablissementPage"
);
const MesEntreprisesTravauxPage = lazyNamed(
  () => import("./components/pages/MesEntreprisesTravauxPage"),
  "MesEntreprisesTravauxPage"
);
const PrixAdminPage = lazyNamed(
  () => import("./components/pages/PrixAdminPage"),
  "PrixAdminPage"
);
const ReferentielsAdminPage = lazyNamed(
  () => import("./components/pages/ReferentielsAdminPage"),
  "ReferentielsAdminPage"
);
const MesDocumentsPage = lazyNamed(
  () => import("./components/pages/MesDocumentsPage"),
  "MesDocumentsPage"
);
const DocumentsAdminPage = lazyNamed(
  () => import("./components/pages/DocumentsAdminPage"),
  "DocumentsAdminPage"
);
const MesDemandesPage = lazyNamed(
  () => import("./components/pages/MesDemandesPage"),
  "MesDemandesPage"
);
const MonProfilPage = lazyNamed(
  () => import("./components/pages/MonProfilPage"),
  "MonProfilPage"
);
const SupervisionAdminPage = lazyNamed(
  () => import("./components/pages/SupervisionAdminPage"),
  "SupervisionAdminPage"
);
const RgpdAdminPage = lazyNamed(
  () => import("./components/pages/RgpdAdminPage"),
  "RgpdAdminPage"
);

// Wrapping des pages avec le HOC de navigation
const TableauDeBordAdminWrapped = withNavigation(TableauDeBordAdminPage);
const TableauDeBordWrapped = withNavigation(TableauDeBordPage);
const MesChantiersWrapped = withNavigation(MesChantiersPage);
const ChantierDetailAdminWrapped = withNavigation(ChantierDetailAdminPage, (params, role) => ({
  chantierId: params.id,
  userRole: role === "admin" ? "admin" : "user",
  isEditable: true,
}));
const ChantierDetailWrapped = withNavigation(ChantierDetailPage, (params) => ({
  chantierId: params.id,
}));
const ChantierCreationWrapped = withNavigation(ChantierCreationPage);
const DechetFormulaireCreationWrapped = withNavigation(DechetFormulaireCreationPage, (params) => ({
  chantierId: params.id,
}));
const ChantierFormulaireCommandeWrapped = withNavigation(ChantierFormulaireCommandePage, (params) => ({
  chantierId: params.id,
}));
const ChantierFormulaireDevisWrapped = withNavigation(ChantierFormulaireDevisPage);
const MesTransportsWrapped = withNavigation(MesTransportsPage);
const TransportCreationWrapped = withNavigation(TransportCreationPage);
const TransportFormulaireDevisWrapped = withNavigation(TransportFormulairePage, (params) => ({
  mode: "devis" as const,
  id: params.id,
}));
const TransportFormulaireCommandeWrapped = withNavigation(TransportFormulairePage, (params) => ({
  mode: "commande" as const,
  id: params.id,
}));
const TransportDetailAdminWrapped = withNavigation(TransportDetailAdminPage, (params) => ({
  transportId: params.id,
}));
const TransportDetailWrapped = withNavigation(TransportDetailPage, (params) => ({
  transportId: params.id,
}));
const MesCAPWrapped = withNavigation(MesCAPPage);
const MaFacturationWrapped = withNavigation(MaFacturationPage);
const FacturationAdminWrapped = withNavigation(FacturationAdminPage);
const OperationsAdminWrapped = withNavigation(OperationsAdminPage, (params) => ({
  defaultTab: params.tab,
}));
const EtablissementsAdminWrapped = withNavigation(EtablissementsAdminPage, (params) => ({
  etablissementId: params.id,
}));
const MonEtablissementWrapped = withNavigation(MonEtablissementPage);
const MesEntreprisesTravauxWrapped = withNavigation(MesEntreprisesTravauxPage);
const PrixAdminWrapped = withNavigation(PrixAdminPage);
const ReferentielsAdminWrapped = withNavigation(ReferentielsAdminPage);
const MesDocumentsWrapped = withNavigation(MesDocumentsPage);
const DocumentsAdminWrapped = withNavigation(DocumentsAdminPage);
const MesDemandesWrapped = withNavigation(MesDemandesPage);
const MonProfilWrapped = withNavigation(MonProfilPage);
const SupervisionAdminWrapped = withNavigation(SupervisionAdminPage);
const RgpdAdminWrapped = withNavigation(RgpdAdminPage);
const NotFoundPage = lazyNamed(
  () => import("./components/pages/NotFoundPage"),
  "NotFoundPage"
);

function useIsAdmin() {
  const { role, appRole } = useAuthContext();
  return appRole === "admin_serme" || role === "admin";
}

function AdminOnlyRoute({ children }: { children: React.ReactElement }) {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function ClientOnlyRoute({ children }: { children: React.ReactElement }) {
  const isAdmin = useIsAdmin();
  if (isAdmin) return <Navigate to="/" replace />;
  return children;
}

// Composants qui switchent par role
function DashboardSwitch() {
  const isAdmin = useIsAdmin();
  return isAdmin ? <TableauDeBordAdminWrapped /> : <TableauDeBordWrapped />;
}

function ChantierDetailSwitch() {
  const isAdmin = useIsAdmin();
  return isAdmin ? <ChantierDetailAdminWrapped /> : <ChantierDetailWrapped />;
}

function TransportDetailSwitch() {
  const isAdmin = useIsAdmin();
  // R1021-3 : un lien de chaine (/transports/A -> /transports/B) remonte la page, onglet et motif d'annulation compris.
  const { id } = useParams();
  return isAdmin ? <TransportDetailAdminWrapped key={id} /> : <TransportDetailWrapped />;
}

function FacturationSwitch() {
  const isAdmin = useIsAdmin();
  return isAdmin ? <FacturationAdminWrapped /> : <MaFacturationWrapped />;
}

// Enveloppe commune a toutes les routes (ex-contenu de BrowserRouter).
function RacineApp() {
  return (
    <>
      <Toaster position="top-right" richColors />
      <ErrorBoundary fallback={(error) => <AppErrorFallback error={error} />}>
        <Suspense fallback={<LoadingPanel className="h-screen" />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

// Routeur « data » : seul mode ou useBlocker existe (D-QUITTER-SANS-ENREGISTRER, RC3.06).
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RacineApp />}>
      <Route path="/login" element={<ConnexionPage />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/mfa-enroll" element={<MfaEnrollPage />} />
      <Route path="/mfa-challenge" element={<MfaChallengePage />} />
      <Route path="/cgu-acceptation" element={<CguAcceptationPage />} />

      {/* Pages legales — publiques (hors AuthLayout) */}
      <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
      <Route path="/cgu" element={<ConditionsGeneralesPage />} />
      <Route path="/confidentialite" element={<PolitiqueConfidentialitePage />} />

      {/* Routes authentifiees */}
      <Route element={<AuthLayout />}>
        {/* Dashboard — switch par role */}
        <Route path="/" element={<DashboardSwitch />} />

        {/* Chantiers */}
        <Route path="/chantiers" element={<ClientOnlyRoute><MesChantiersWrapped /></ClientOnlyRoute>} />
        <Route path="/chantiers/nouveau" element={<ChantierCreationWrapped />} />
        <Route path="/chantiers/devis" element={<ChantierFormulaireDevisWrapped />} />
        <Route path="/chantiers/commande" element={<ChantierFormulaireCommandeWrapped />} />
        <Route path="/chantiers/:id" element={<ChantierDetailSwitch />} />
        <Route path="/chantiers/:id/dechet/nouveau" element={<DechetFormulaireCreationWrapped />} />
        <Route path="/chantiers/:id/commande" element={<ChantierFormulaireCommandeWrapped />} />
        <Route path="/chantiers/:id/devis" element={<ChantierFormulaireDevisWrapped />} />

        {/* Transports */}
        <Route path="/transports" element={<ClientOnlyRoute><MesTransportsWrapped /></ClientOnlyRoute>} />
        <Route path="/transports/nouveau" element={<TransportCreationWrapped />} />
        <Route path="/transports/devis" element={<TransportFormulaireDevisWrapped />} />
        <Route path="/transports/commande" element={<TransportFormulaireCommandeWrapped />} />
        <Route path="/transports/:id/commande" element={<TransportFormulaireCommandeWrapped />} />
        <Route path="/transports/:id/devis" element={<TransportFormulaireDevisWrapped />} />
        <Route path="/transports/:id" element={<TransportDetailSwitch />} />

        {/* CAP */}
        <Route path="/cap" element={<ClientOnlyRoute><MesCAPWrapped /></ClientOnlyRoute>} />

        {/* Facturation — switch par role */}
        <Route path="/devis-factures" element={<FacturationSwitch />} />

        {/* Operations — admin only */}
        <Route path="/operations" element={<AdminOnlyRoute><OperationsAdminWrapped /></AdminOnlyRoute>} />
        <Route path="/operations/:tab" element={<AdminOnlyRoute><OperationsAdminWrapped /></AdminOnlyRoute>} />

        {/* Etablissements — admin only, client voit /mon-etablissement */}
        <Route path="/etablissements" element={<AdminOnlyRoute><EtablissementsAdminWrapped /></AdminOnlyRoute>} />
        <Route path="/mon-etablissement" element={<ClientOnlyRoute><MonEtablissementWrapped /></ClientOnlyRoute>} />
        <Route path="/mes-entreprises" element={<ClientOnlyRoute><MesEntreprisesTravauxWrapped /></ClientOnlyRoute>} />

        {/* Prix — admin only */}
        <Route path="/prix" element={<AdminOnlyRoute><PrixAdminWrapped /></AdminOnlyRoute>} />

        {/* Referentiels — admin only */}
        <Route path="/types-categories" element={<AdminOnlyRoute><ReferentielsAdminWrapped /></AdminOnlyRoute>} />

        {/* Documents */}
        <Route path="/documents" element={<ClientOnlyRoute><MesDocumentsWrapped /></ClientOnlyRoute>} />
        <Route path="/documents-admin" element={<AdminOnlyRoute><DocumentsAdminWrapped /></AdminOnlyRoute>} />

        {/* Demandes */}
        <Route path="/demandes" element={<ClientOnlyRoute><MesDemandesWrapped /></ClientOnlyRoute>} />

        {/* Profil */}
        <Route path="/profil" element={<MonProfilWrapped />} />

        {/* Supervision — admin SERME only (T0620.07) : onglets Emails envoyes /
            Synchronisation TD / Maintenance. Remplace /maintenance et /emails-envoyes. */}
        <Route path="/supervision" element={<AdminOnlyRoute><SupervisionAdminWrapped /></AdminOnlyRoute>} />

        {/* Guichet RGPD — admin SERME only, page hors menu (Lot 7) */}
        <Route path="/rgpd" element={<AdminOnlyRoute><RgpdAdminWrapped /></AdminOnlyRoute>} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Route>
  )
);

// Dev : un rechargement a chaud recree le routeur ; liberer l'ancien (ecouteur popstate).
import.meta.hot?.dispose(() => router.dispose());

function App() {
  return <RouterProvider router={router} />;
}

export default App;
